import React from "react";
import { TimeSeries, AlignMethod } from "../../common/src";
import { numberFormatFun, numberFormat } from "../../common/src/utils/FormatFunctions";
import { Language } from "./Language";

const format = numberFormatFun("0.0%");
const integerFormat = numberFormatFun("0.00");

export enum PerformanceKeyEnum {
    Value = "Value",
    YearlyReturn = "YearlyReturn",
    Return = "Return",
    Volatility = "Volatility",
    ShareOfPositiveMonths = "ShareOfPositiveMonths",
    Correlation = "Correlation",
    Beta = "Beta",
    TrackingError = "TrackingError",
    InformationRatio = "InformationRatio",
    SharpeRatio = "SharpeRatio"
}

const PerformanceKeyEnglish = {
    [PerformanceKeyEnum.Value]: "Value",
    [PerformanceKeyEnum.YearlyReturn]: "Yearly return",
    [PerformanceKeyEnum.Return]: "Return",
    [PerformanceKeyEnum.Volatility]: "Volatility",
    [PerformanceKeyEnum.ShareOfPositiveMonths]: "Share of positive months",
    [PerformanceKeyEnum.Correlation]: "Correlation",
    [PerformanceKeyEnum.Beta]: "Beta",
    [PerformanceKeyEnum.TrackingError]: "Tracking error",
    [PerformanceKeyEnum.InformationRatio]: "Information ratio",
    [PerformanceKeyEnum.SharpeRatio]: "Sharpe ratio"
};

const PerformanceKeySwedish = {
    [PerformanceKeyEnum.Value]: "Värde",
    [PerformanceKeyEnum.YearlyReturn]: "Årsavkastning",
    [PerformanceKeyEnum.Return]: "Enkel avkastning",
    [PerformanceKeyEnum.Volatility]: "Volatilitet",
    [PerformanceKeyEnum.ShareOfPositiveMonths]: "Andel positiva månader",
    [PerformanceKeyEnum.Correlation]: "Korrelation",
    [PerformanceKeyEnum.Beta]: "Beta",
    [PerformanceKeyEnum.TrackingError]: "Aktiv risk",
    [PerformanceKeyEnum.InformationRatio]: "Informationskvot",
    [PerformanceKeyEnum.SharpeRatio]: "Sharpekvot"
};

const translatePerformanceKey = (locale: Language, input: PerformanceKeyEnum): string => {
    if (locale === Language.Sv) {
        return PerformanceKeySwedish[input];
    }
    const result = PerformanceKeyEnglish[input];
    return result;
};

type KeyPerformanceIndicatorsInput = {
    masterTimeSeriesName: string;
    timeseries: TimeSeries[];
    navValue?: number;
    riskFreeTimeSeries?: TimeSeries;
    locale?: Language;
    showItems?: PerformanceKeyEnum[];
    useExcessReturnSharpe?: boolean;
};

export const KeyPerformanceIndicators = ({
    masterTimeSeriesName,
    timeseries,
    navValue,
    riskFreeTimeSeries,
    locale = Language.Sv,
    showItems = [
        PerformanceKeyEnum.Value,
        PerformanceKeyEnum.YearlyReturn,
        PerformanceKeyEnum.Return,
        PerformanceKeyEnum.Volatility,
        PerformanceKeyEnum.ShareOfPositiveMonths,
        PerformanceKeyEnum.Correlation,
        PerformanceKeyEnum.Beta,
        PerformanceKeyEnum.TrackingError,
        PerformanceKeyEnum.InformationRatio,
        PerformanceKeyEnum.SharpeRatio
    ],
    useExcessReturnSharpe = false
}: KeyPerformanceIndicatorsInput): React.ReactElement => {
    const masterTs = timeseries.filter((e) => e.name === masterTimeSeriesName)[0];
    const masterTsResampled = masterTs.resampleToCalenderMonthly();

    const correlation: string[] = [];
    const beta: string[] = [];
    const trackingError: string[] = [];
    const value: string[] = [];
    const informationRatio: string[] = [];
    const sharpeRatio: string[] = [];
    const annualizedReturn: string[] = [];
    const simpleReturn: number[] = [];

    for (let i = 0; i < timeseries.length; i++) {
        const timeSerie = timeseries[i].resampleToCalenderMonthly();
        
        let sharpeRatioExcess;
        if (useExcessReturnSharpe && riskFreeTimeSeries) {
            const alignedriskFreeTimeSeries = TimeSeries.align(timeseries[i], riskFreeTimeSeries, AlignMethod.Latest);
            const excessReturn = timeSerie
                .return()
                .subtract(alignedriskFreeTimeSeries.resampleToCalenderMonthly().return())
                .add(1)
                .cumProd();
            sharpeRatioExcess = integerFormat(excessReturn.sharpeRatio());
    }

        if (timeSerie.name === masterTs.name) {
            correlation[i] = "";
            beta[i] = "";
            trackingError[i] = "";
            value[i] = numberFormat(navValue, "# ##0");
            informationRatio[i] = "";
            sharpeRatio[i] = sharpeRatioExcess ? sharpeRatioExcess : integerFormat(masterTsResampled.sharpeRatio());
            annualizedReturn[i] = format(masterTsResampled.annualizedReturn());
        } else {
            const benchMarkReturn = timeSerie.annualizedReturn();
            correlation[i] = integerFormat(timeSerie.correlation(masterTsResampled));
            trackingError[i] = format(timeSerie.trackingError(masterTsResampled));
            beta[i] = integerFormat((timeSerie.correlation(masterTsResampled) * masterTsResampled.volatility()) / timeSerie.volatility());
            value[i] = "";
            informationRatio[i] = integerFormat(masterTsResampled.informationRatio(timeSerie));
            sharpeRatio[i] = sharpeRatioExcess ? sharpeRatioExcess : integerFormat(timeSerie.sharpeRatio());
            annualizedReturn[i] = format(benchMarkReturn) + " [" + format(masterTsResampled.annualizedReturn() - benchMarkReturn) + "]";
        }

        simpleReturn[i] = timeSerie.valueReturn();
    }

    // Build rows data
    const rows: { key: string; values: string[] }[] = [];

    if (showItems.includes(PerformanceKeyEnum.Value)) {
        rows.push({ key: translatePerformanceKey(locale, PerformanceKeyEnum.Value), values: value });
    }
    if (showItems.includes(PerformanceKeyEnum.YearlyReturn)) {
        rows.push({
            key: translatePerformanceKey(locale, PerformanceKeyEnum.YearlyReturn),
            values: annualizedReturn
        });
    }
    if (showItems.includes(PerformanceKeyEnum.Return)) {
        rows.push({
            key: translatePerformanceKey(locale, PerformanceKeyEnum.Return),
            values: timeseries.map((ts) => format(ts.resampleToCalenderMonthly().valueReturn()) ?? "")
        });
    }
    if (showItems.includes(PerformanceKeyEnum.Volatility)) {
        rows.push({
            key: translatePerformanceKey(locale, PerformanceKeyEnum.Volatility),
            values: timeseries.map((ts) => format(ts.resampleToCalenderMonthly().volatility()) ?? "")
        });
    }
    if (showItems.includes(PerformanceKeyEnum.ShareOfPositiveMonths)) {
        rows.push({
            key: translatePerformanceKey(locale, PerformanceKeyEnum.ShareOfPositiveMonths),
            values: timeseries.map((ts) => format(ts.resampleToMonthly().positiveShare()))
        });
    }
    if (showItems.includes(PerformanceKeyEnum.Correlation)) {
        rows.push({ key: translatePerformanceKey(locale, PerformanceKeyEnum.Correlation), values: correlation });
    }
    if (showItems.includes(PerformanceKeyEnum.TrackingError)) {
        rows.push({ key: translatePerformanceKey(locale, PerformanceKeyEnum.TrackingError), values: trackingError });
    }
    if (showItems.includes(PerformanceKeyEnum.InformationRatio)) {
        rows.push({ key: translatePerformanceKey(locale, PerformanceKeyEnum.InformationRatio), values: informationRatio });
    }
    if (showItems.includes(PerformanceKeyEnum.SharpeRatio)) {
        rows.push({ key: translatePerformanceKey(locale, PerformanceKeyEnum.SharpeRatio), values: sharpeRatio });
    }

    const headers = timeseries.map((ts) => ts.name);

    return (
        <div className="my-3.5">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-white">
                        <th className="py-1.5 px-2.5 text-left font-bold"></th>
                        {headers.map((header, i) => (
                            <th key={i} className="py-1.5 px-2.5 text-left font-bold">
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? "bg-gray-100" : "bg-white"}>
                            <td className="py-1.5 px-2.5 font-bold">{row.key}</td>
                            {row.values.map((val, colIndex) => (
                                <td key={colIndex} className="py-1.5 px-2.5">
                                    {val}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
